import React, { useState } from 'react';
import { Plus, Trash2, Edit, CircleX, CircleCheck, Play } from 'lucide-react';
import { MediaLink, Quality } from '@/types/model';
import { TableAction } from '@/types';
import { useTranslation } from '@/hooks';
import Button from './Button';
import LanguageList from './LanguageList';
import DropdownActions from './DropdownActions';
import LinkManagerModal from '@/components/ui/LinkManagerModal.tsx';

type DisplayableLink = {
  id?: string;
  quality: Quality;
  url: string;
  audioLanguages?: string[];
  subtitleLanguages?: string[];
  active: boolean;
};

interface LinkManagerProps {
  links: DisplayableLink[];
  onAddLink: (link: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => void;
  onUpdateLink?: (linkId: string, link: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => void;
  onDeleteLink?: (linkId: string) => void;
  onToggleLink?: (linkId: string, active: boolean) => void;
  onPlayLink?: (link: DisplayableLink) => void;
  error?: string;
}

const LinkManager: React.FC<LinkManagerProps> = ({
                                                   links = [],
                                                   onAddLink,
                                                   onUpdateLink,
                                                   onDeleteLink,
                                                   onToggleLink,
                                                   onPlayLink,
                                                   error
                                                 }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<DisplayableLink | null>(null);


  const handleDeleteLink = (linkId: string) => {
    onDeleteLink?.(linkId);
  };

  const handleEditLink = (link: DisplayableLink) => {
    setEditingLink(link);
    setIsModalOpen(true);
  };

  const handleToggleLink = (linkId: string, currentStatus: boolean) => {
    onToggleLink?.(linkId, !currentStatus);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingLink(null);
  };

  const getActions = (link: DisplayableLink): TableAction<DisplayableLink>[] => {
    const actions: TableAction<DisplayableLink>[] = [
      {
        label: t.buttons.edit,
        icon: Edit,
        onClick: (link) => handleEditLink(link),
        className: 'text-blue-600 hover:text-blue-800'
      }
    ];

    if (onToggleLink && link.id) {
      actions.push({
        label: link.active ? t.links.deactivate : t.links.activate,
        icon: link.active ? CircleX : CircleCheck,
        onClick: (link) => link.id && handleToggleLink(link.id, link.active),
        className: link.active ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'
      });
    }

    if (onDeleteLink && link.id) {
      actions.push({
        label: t.links.deleteLink,
        icon: Trash2,
        onClick: (link) => link.id && handleDeleteLink(link.id),
        className: 'text-red-600 hover:text-red-800'
      });
    }

    return actions;
  };

  return (
    <div className="pt-6 mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-gray-900">
          {t.links.title}
        </h3>
        <Button
          type="button"
          variant="warning"
          size="sm"
          onClick={() => setIsModalOpen(true)}
          className="flex items-center space-x-2"
        >
          <Plus className="h-4 w-4"/>
          <span>{t.buttons.addLink}</span>
        </Button>
      </div>

      {/* Links Table */}
      <div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
          <tr>
            {onPlayLink && (
              <th className="w-12 px-2 py-3 text-center">
                <span className="sr-only">{t.vodPlayer.playLink}</span>
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t.links.quality}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t.links.url}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t.links.audioLanguage}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t.links.subtitleLanguage}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t.links.status}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {t.links.actions}
            </th>
          </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">

          {error && (
            <tr>
              <td colSpan={onPlayLink ? 7 : 6} className="px-4 py-8 text-center text-red-600">
                {error}
              </td>
            </tr>
          )}
          {links.length === 0 ? (
            <tr>
              {!error && <td colSpan={onPlayLink ? 7 : 6} className="px-4 py-8 text-center text-gray-500">
                {t.links.noLinks}
              </td>}
            </tr>
          ) : (
            links.map((link) => (
              <tr key={link.id}>
                {onPlayLink && (
                  <td className="w-12 px-2 py-3 text-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => onPlayLink(link)}
                      disabled={!link.active || !link.url.trim()}
                      title={link.active && link.url.trim() ? t.vodPlayer.playLink : t.vodPlayer.noMedia}
                      aria-label={link.active && link.url.trim()
                        ? `${t.vodPlayer.playLink}: ${link.quality}`
                        : t.vodPlayer.noMedia}
                      className="h-8 w-8 text-indigo-600 hover:border-indigo-300 hover:text-indigo-800 focus:ring-2 focus:ring-indigo-500"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-gray-900">{link.quality}</td>
                <td className="px-4 py-3 text-sm text-blue-600 truncate max-w-xs">
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.url}
                  </a>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <LanguageList languages={link.audioLanguages || []}/>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <LanguageList languages={link.subtitleLanguages || []}/>
                </td>
                <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      link.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {link.active ? t.links.active : t.links.inactive}
                    </span>
                </td>
                <td className="px-4 py-3">
                  <DropdownActions
                    actions={getActions(link)}
                    item={link}
                  />
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>
      <LinkManagerModal
        isModalOpen={isModalOpen}
        handleModalClose={handleModalClose}
        handleAddLink={onAddLink}
        handleUpdateLink={onUpdateLink}
        editingLink={editingLink}/>
    </div>
  );
};

export default LinkManager;
